
import { Component, Prop, Vue } from 'vue-property-decorator';
import { AxiosError } from 'axios';
import { Website } from '@/services/Website/interfaces';
import { FtpUserRepositoryInterface } from '@/services/FtpUser/interfaces';
import { DefaultFtpUserFieldRules } from '@/services/FtpUser/forms/CreateFtpUserForm';
import { ServerRepositoryInterface } from '@/services/Server/Interfaces';
import { SystemUserRepositoryInterface } from '@/services/SystemUsers/interfaces';

@Component({
  inject: [
    'ftpUserRepository',
    'serverRepository',
    'systemUserRepository',
  ],
})
export default class CreateFtpUserDialog extends Vue {
  @Prop({ required: true }) public readonly website!: Website;

  public visible: boolean = true;

  public loading: boolean = true;

  public submitting: boolean = false;

  public serverHostname: string = '';

  public homeDir: string = '';

  public systemUserId: number = -1;

  public username: string = '';

  public password: string = '';

  public usernameRules = DefaultFtpUserFieldRules.username;

  public passwordRules = DefaultFtpUserFieldRules.password;

  public homeDirRules = DefaultFtpUserFieldRules.homeDir;

  private readonly ftpUserRepository!: FtpUserRepositoryInterface;

  private readonly serverRepository!: ServerRepositoryInterface;

  private readonly systemUserRepository!: SystemUserRepositoryInterface;

  public get canSubmit(): boolean {
    if (!this.username || !this.password || !this.homeDir || this.systemUserId < 1) {
      return false;
    }

    const usernameValid = this.usernameRules.every((rule) => rule(this.username) === true);
    const passwordValid = this.passwordRules.every((rule) => rule(this.password) === true);
    const homeDirValid = this.homeDirRules.every((rule) => rule(this.homeDir) === true);

    return usernameValid && passwordValid && homeDirValid;
  }

  public async created(): Promise<void> {
    try {
      await this.preloadSiteData();
    } catch (err) {
      this.handleError(err);
    } finally {
      this.loading = false;
    }
  }

  private async preloadSiteData(): Promise<void> {
    const unixUserId = this.website.details?.unixUserId;
    if (!unixUserId) {
      throw new Error('Website has no unix user configured.');
    }

    this.systemUserId = unixUserId;

    const [server, systemUser] = await Promise.all([
      this.serverRepository.getServer(this.website.originServer),
      this.systemUserRepository.findSystemUserById(unixUserId),
    ]);

    this.serverHostname = server.hostname || this.website.originServer;

    const domain = this.website.domain.startsWith('xn--')
      ? this.website.domain.replace('xn--', '')
      : this.website.domain;

    this.homeDir = systemUser.websiteId === this.website.id
      ? `${systemUser.directory}/public_html`
      : `${systemUser.directory}/domains/${domain}/public_html`;
  }

  public async createFtpUser(): Promise<void> {
    if (!this.canSubmit) {
      return;
    }

    this.submitting = true;
    try {
      await this.ftpUserRepository.createFtpUser({
        server: this.website.originServer,
        websiteId: this.website.id,
        systemUserId: this.systemUserId,
        username: this.username,
        password: this.password,
        homeDir: this.homeDir,
      });

      this.$successDialog.show(`FTP User ${this.username} creation started!`);
      this.$emit('created');
      this.destroyComponent();
    } catch (err) {
      this.handleError(err);
    } finally {
      this.submitting = false;
    }
  }

  public destroyComponent(): void {
    this.$emit('close');
  }

  private handleError(err: unknown): void {
    const axiosErr = err as AxiosError;
    if (axiosErr.isAxiosError) {
      this.$errorDialog.show(axiosErr.response?.data?.message || axiosErr.message);
    } else if (err instanceof Error) {
      this.$errorDialog.show(err.message);
    }
  }
}
